import { http } from "./http";

/**
 * IVR application as shown on the Applications overview. Mirrors
 * ApplicationListItemDto in the .NET ApplicationsController.
 *
 * - dnisNumbers: phone numbers currently routing into this application.
 *   Empty list when the application is unassigned.
 * - modifiedUtc: last modification of the application's ACTIVE flow
 *   (initialFlowId), not the application row itself.
 */
export interface ApplicationListItem {
  applicationId: number;
  applicationName: string;
  stateName: string;
  activeCount: number;
  modifiedUtc: string | null;
  dnisNumbers: string[];
}

export interface ApplicationListResponse {
  items: ApplicationListItem[];
}

/**
 * Returned by the create endpoint after spCustomerApplication_Create
 * provisions a fresh application + initial draft flow. Only the IDs we
 * need for navigation/refresh are guaranteed; the full row is read on
 * the next list/refresh.
 */
export interface ApplicationCreated {
  applicationId: number;
  applicationName: string;
  initialFlowId: number;
}

/**
 * Per-application detail row backing /api/applications/{id}. Mirrors
 * ApplicationDetailDto on the API. The SPA uses this for the Application
 * detail page (editable name + description, "Build" button driven by
 * initialFlowId, metadata stripe).
 */
export interface ApplicationDetail {
  applicationId: number;
  applicationName: string;
  description: string;
  initialFlowId: number | null;
  createdUtc: string;
  modifiedUtc: string | null;
}

/**
 * One ApplicationDnis schedule row from the application's perspective.
 * Mirrors ApplicationScheduleItemDto on the API. Note: the linked DNIS
 * is identified by id + presentation (digits-only), not the application
 * name like the dnis-side schedule — that's the inversion this surface
 * is built around.
 */
export interface ApplicationScheduleItem {
  applicationDnisId: number;
  applicationId: number;
  dnisId: number;
  dnisPresentation: string;
  stateColor: string;
  stateName: "Active" | "Scheduled" | "Expired" | string;
  allowDelete: boolean;
  validFromUtc: string;
}

export interface ApplicationScheduleResponse {
  applicationId: number;
  items: ApplicationScheduleItem[];
}

export const applicationsApi = {
  list: () => http<ApplicationListResponse>("/api/applications"),
  create: (applicationName: string) =>
    http<ApplicationCreated>("/api/applications", {
      method: "POST",
      body: JSON.stringify({ applicationName }),
    }),
  getDetail: (applicationId: number) =>
    http<ApplicationDetail>(`/api/applications/${applicationId}`),
  update: (
    applicationId: number,
    payload: { applicationName: string; description: string },
  ) =>
    http<ApplicationDetail>(`/api/applications/${applicationId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  /**
   * Soft-delete the application. The API returns 200 when the row was
   * deleted (or had already been deleted) and 409 with
   * <code>{ error: "has-active-or-future-bindings" }</code> when active
   * or future ApplicationDnis bindings still reference the app — those
   * must be removed from the schedule first.
   */
  remove: (applicationId: number) =>
    http<void>(`/api/applications/${applicationId}`, { method: "DELETE" }),
  getSchedule: (applicationId: number) =>
    http<ApplicationScheduleResponse>(
      `/api/applications/${applicationId}/schedule`,
    ),
  addSchedule: (
    applicationId: number,
    payload: { dnisId: number; validFromUtc?: string | null },
  ) =>
    http<ApplicationScheduleResponse>(
      `/api/applications/${applicationId}/schedule`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
  deleteSchedule: (applicationId: number, applicationDnisId: number) =>
    http<ApplicationScheduleResponse>(
      `/api/applications/${applicationId}/schedule/${applicationDnisId}`,
      { method: "DELETE" },
    ),
};
